/**
 * <stockist-map> — Leaflet map of stockist locations.
 *
 * Pin data is rendered by sections/stockist-map.liquid from the `stockist`
 * metaobjects — every visible one, including those the sync script could not
 * geocode, which appear in the list with a null lat/lng and no pin. Clicking a
 * pin highlights its list entry and vice versa.
 *
 * Leaflet's default marker needs image assets that Shopify's flat asset CDN
 * would not resolve, so pins are CSS-drawn divIcons instead.
 */
if (!customElements.get('stockist-map')) {
	class StockistMap extends HTMLElement {
		constructor() {
			super();
			this.canvas = this.querySelector('.stockist-map__canvas');
			this.listItems = Array.from(this.querySelectorAll('.stockist-map__list-item'));
			this.searchInput = this.querySelector('.stockist-map__search-input');
			this.searchCount = this.querySelector('.stockist-map__search-count');
			this.noResults = this.querySelector('.stockist-map__no-results');
			this.markers = [];
			this.pinCount = 0;
			this.hasView = false;
			this.map = null;
			this.markerLayer = null;
			this.searchIndex = [];
		}

		// Lowercased, accent-stripped haystack so "Cafe" matches "Café" and the
		// comparison is not tripped by casing.
		static normalise(value) {
			return String(value == null ? '' : value)
				.toLowerCase()
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '');
		}

		connectedCallback() {
			// leaflet.js is deferred, so it may not have parsed yet.
			if (typeof L === 'undefined') {
				window.addEventListener('load', () => this.init(), { once: true });
				return;
			}
			this.init();
		}

		disconnectedCallback() {
			if (this.map) {
				this.map.remove();
				this.map = null;
			}
		}

		get locations() {
			const holder = this.querySelector('.stockist-map__data');
			if (!holder) return [];
			try {
				return JSON.parse(holder.textContent);
			} catch (err) {
				console.error('stockist-map: could not parse pin data', err);
				return [];
			}
		}

		init() {
			if (this.map || typeof L === 'undefined') return;
			const locations = this.locations;
			if (!locations.length) return;

			this.map = L.map(this.canvas, {
				scrollWheelZoom: false,
				zoomControl: true,
			});

			L.tileLayer(this.dataset.tileUrl, {
				maxZoom: 19,
				attribution: this.dataset.attribution || '',
			}).addTo(this.map);

			const icon = L.divIcon({
				className: 'stockist-map__pin',
				html: '<span class="stockist-map__pin-dot"></span>',
				iconSize: [18, 18],
				iconAnchor: [9, 9],
				popupAnchor: [0, -10],
			});

			// Markers live in a layer group so search can pull them off the map
			// and put them back without rebuilding them.
			this.markerLayer = L.layerGroup().addTo(this.map);

			// A stockist the geocoder could not place (PO box, placeholder address)
			// still gets a list row and a search-index slot, but no marker. The
			// null holds its position so markers and list rows keep pairing by
			// index.
			locations.forEach((loc, i) => {
				this.searchIndex.push(StockistMap.normalise(`${loc.name} ${loc.address || ''}`));

				if (loc.lat == null || loc.lng == null) {
					this.markers.push(null);
					return;
				}

				const marker = L.marker([loc.lat, loc.lng], { icon, title: loc.name })
					.bindPopup(this.popupHtml(loc));

				marker.on('click', () => this.highlight(i, { pan: false }));
				this.markers.push(marker);
				this.markerLayer.addLayer(marker);
			});

			const pins = this.markers.filter(Boolean);
			this.pinCount = pins.length;
			this.defaultZoom = parseInt(this.dataset.zoom, 10);
			this.fitTo(pins.map((m) => m.getLatLng()));

			this.listItems.forEach((item, i) => {
				// Rows without a pin are plain divs; nothing to fly to.
				if (!this.markers[i]) return;
				item.addEventListener('click', () => this.highlight(i, { pan: true }));
			});

			this.initSearch();

			// The section can be laid out while hidden (theme editor tab switch,
			// accordion); Leaflet needs a nudge once it has real dimensions.
			if (typeof ResizeObserver !== 'undefined') {
				this.resizeObserver = new ResizeObserver(() => this.map && this.map.invalidateSize());
				this.resizeObserver.observe(this.canvas);
			}
		}

		fitTo(latLngs) {
			// Leaflet throws on every interaction until a view is set, so a list
			// where nothing at all could be geocoded still needs a starting view.
			if (!latLngs.length) {
				if (!this.hasView) {
					this.map.setView([39.5, -98.35], 3);
					this.hasView = true;
				}
				return;
			}
			this.hasView = true;
			const bounds = L.latLngBounds(latLngs);
			// A single match has zero-size bounds; fitBounds would zoom to max.
			if (latLngs.length === 1) {
				this.map.setView(latLngs[0], Math.max(this.defaultZoom || 0, 11));
				return;
			}
			if (this.defaultZoom > 0 && latLngs.length === this.pinCount) {
				this.map.setView(bounds.getCenter(), this.defaultZoom);
			} else {
				this.map.fitBounds(bounds, { padding: [40, 40] });
			}
		}

		initSearch() {
			if (!this.searchInput) return;

			// Ships readonly to keep browser autofill out (see the section
			// template). Unlock on the first real interaction — pointerdown and
			// focus between them cover mouse, touch and keyboard tabbing.
			const unlock = () => {
				if (!this.searchInput.readOnly) return;
				this.searchInput.readOnly = false;
				// Safari keeps the caret out until focus is reapplied.
				if (document.activeElement === this.searchInput) {
					this.searchInput.blur();
					this.searchInput.focus();
				}
			};
			['pointerdown', 'focus', 'touchstart'].forEach((evt) =>
				this.searchInput.addEventListener(evt, unlock)
			);

			let timer = null;
			const run = () => this.applySearch(this.searchInput.value);

			this.searchInput.addEventListener('input', () => {
				clearTimeout(timer);
				timer = setTimeout(run, 150);
			});

			// Escape clears; the native search-field clear button fires 'search'.
			this.searchInput.addEventListener('search', run);
			this.searchInput.addEventListener('keydown', (e) => {
				if (e.key !== 'Escape') return;
				this.searchInput.value = '';
				run();
			});

			this.applySearch('');
		}

		applySearch(rawQuery) {
			const query = StockistMap.normalise(rawQuery).trim();
			// Every whitespace-separated term must appear, so "portland art"
			// narrows rather than widening to anything matching either word.
			const terms = query ? query.split(/\s+/) : [];

			const visible = [];
			this.searchIndex.forEach((haystack, i) => {
				const match = terms.every((t) => haystack.includes(t));
				if (this.listItems[i]) this.listItems[i].hidden = !match;
				if (match) visible.push(i);
			});

			this.markerLayer.clearLayers();
			const visiblePins = [];
			visible.forEach((i) => {
				const marker = this.markers[i];
				if (!marker) return;
				this.markerLayer.addLayer(marker);
				visiblePins.push(marker.getLatLng());
			});

			if (this.noResults) this.noResults.hidden = visible.length > 0;
			if (this.searchCount) {
				this.searchCount.textContent = terms.length
					? `${visible.length} of ${this.markers.length}`
					: '';
			}

			if (visiblePins.length) {
				this.fitTo(visiblePins);
			}
		}

		popupHtml(loc) {
			const parts = [`<strong>${this.escape(loc.name)}</strong>`];
			if (loc.address) {
				parts.push(`<span>${this.escape(loc.address).replace(/\n/g, '<br>')}</span>`);
			}
			if (loc.website) {
				const label = loc.website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
				parts.push(
					`<a href="${this.escape(loc.website)}" target="_blank" rel="noopener">${this.escape(label)}</a>`
				);
			}
			return `<div class="stockist-map__popup">${parts.join('')}</div>`;
		}

		escape(value) {
			const div = document.createElement('div');
			div.textContent = value == null ? '' : String(value);
			return div.innerHTML;
		}

		highlight(index, { pan }) {
			const marker = this.markers[index];

			if (pan && marker) {
				this.map.panTo(marker.getLatLng());
				marker.openPopup();
			}

			this.listItems.forEach((item, i) => item.classList.toggle('is-active', i === index));

			const active = this.listItems[index];
			if (active && !pan) {
				active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
			}
		}
	}

	customElements.define('stockist-map', StockistMap);
}
